import { CardColor, GameState } from '../types';
import { xmur3 } from './random';

const cardSignature = (card: any) => ({
  id: card.id,
  type: card.type,
  value: card.value,
  color: card.color,
  typeB: card.typeB,
  valueB: card.valueB,
  essenceCost: card.essenceCost,
  ownerId: card.ownerId,
  subType: card.subType,
  extendedShield: card.extendedShield,
  playableAfterPass: card.playableAfterPass,
  appliedRound: card.appliedRound,
  faceSlug: card.faceSlug,
})

const preparedCardSignature = (prepared: any) => ({
  card: cardSignature(prepared.card),
  rotation: prepared.rotation,
  lockedState: prepared.lockedState ?? null,
  mageHandCast: prepared.mageHandCast ?? false,
})

const privatePreparedCardSignature = (prepared: any) => ({
  card: cardSignature(prepared.card),
  lockedState: prepared.lockedState ?? null,
  mageHandCast: prepared.mageHandCast ?? false,
})

const activationSignature = (activation: any) => ({
  initiatorId: activation.initiatorId,
  attackingCard: preparedCardSignature(activation.attackingCard),
  counterChain: activation.counterChain.map(preparedCardSignature),
  state: activation.state,
  targetId: activation.targetId,
  resolved: activation.resolved ?? false,
  accepted: activation.accepted ?? false,
  wasCountered: activation.wasCountered ?? false,
  winningCard: activation.winningCard ? preparedCardSignature(activation.winningCard) : null,
  partiallyCounteredCard: activation.partiallyCounteredCard ? preparedCardSignature(activation.partiallyCounteredCard) : null,
  parentActivationIndex: activation.parentActivationIndex,
  childActivationIndices: activation.childActivationIndices ?? [],
  treeDepth: activation.treeDepth,
})

export const computeStateChecksum = (state: GameState, privatePlayerId?: number): string => {
  const p = state.players.map(pl => ({
    id: pl.id,
    name: pl.name,
    health: pl.health,
    essence: pl.essence,
    shield: pl.shield,
    maxHealth: pl.maxHealth,
    maxPrepareSlots: pl.maxPrepareSlots,
    disruptedSlots: pl.disruptedSlots,
    towers: pl.towers.map(t => ({ c: t.color, id: t.id })),
    // Hand order is a local display preference, not shared game state.
    hand: pl.hand.map(cardSignature).sort((a, b) => a.id.localeCompare(b.id)),
    prepared: pl.prepared.map(prepared => pl.id === privatePlayerId
      ? privatePreparedCardSignature(prepared)
      : preparedCardSignature(prepared)),
    resolved: pl.resolved.map(preparedCardSignature),
    effects: pl.effectCards.map(cardSignature),
    capturedTowerThisTurn: pl.capturedTowerThisTurn,
    bonusDraw: pl.bonusDraw,
    hasPassedActiveTurn: pl.hasPassedActiveTurn,
    hero: pl.hero,
    playerClass: pl.playerClass,
    hasChosenHeroAndClass: pl.hasChosenHeroAndClass,
    astridCharges: pl.astridCharges ?? null,
    norimCharges: pl.norimCharges ?? null,
    famePoints: pl.famePoints ?? null,
    shanuHibernationStreak: pl.shanuHibernationStreak ?? null,
    shanuLastHibernationRound: pl.shanuLastHibernationRound ?? null,
  }))
  const payload = JSON.stringify({
    seed: state.seed,
    round: state.round,
    phase: state.phase,
    turn: state.turnPlayerId,
    firstPlayerIdForNight: state.firstPlayerIdForNight,
    heroClassTurnId: state.heroClassTurnId,
    consecutivePasses: state.consecutivePasses,
    players: p,
    colorDecks: Object.values(CardColor).reduce((acc, c) => ({ ...acc, [c]: state.colorDecks[c]?.map(cardSignature) || [] }), {}),
    dealingDeck: state.dealingDeck.map(cardSignature),
    stashDeck: state.stashDeck.map(cardSignature),
    stashDiscard: state.stashDiscard.map(cardSignature),
    burnedStashCards: state.burnedStashCards.map(cardSignature),
    stashChoiceOptions: Object.entries(state.stashChoiceOptions || {}).reduce((acc, [playerId, cards]) => ({ ...acc, [playerId]: cards.map(cardSignature) }), {}),
    stashChoicePicksRemaining: state.stashChoicePicksRemaining ?? null,
    availableTowers: state.availableTowers,
    drawEvents: state.drawEvents,
    discardMode: state.discardMode ?? null,
    activations: state.activations.map(activationSignature),
    eliminatedCards: state.eliminatedCards.map(cardSignature),
    purificationInitiatorId: state.purificationInitiatorId,
    overflowPlayerId: state.overflowPlayerId,
    overflowMaxSlots: state.overflowMaxSlots,
    chromabowInitiatorId: state.chromabowInitiatorId,
    hibernationInitiatorId: state.hibernationInitiatorId,
    burglaryInitiatorId: state.burglaryInitiatorId,
    burglaryTargetId: state.burglaryTargetId,
    pendingExtraDayCount: state.pendingExtraDayCount ?? 0,
    extraDaysInCurrentRound: state.extraDaysInCurrentRound ?? 0,
    preparedAtNightStart: state.preparedAtNightStart ?? null,
    damageCardsUsedThisNight: state.damageCardsUsedThisNight ?? null,
    attemptedDamageCardsThisNight: state.attemptedDamageCardsThisNight ?? null,
    pendingVictory: state.pendingVictory,
    acceptedActivations: state.acceptedActivations ?? [],
    lastCardPlayerId: state.lastCardPlayerId ?? null,
    brokenSecretState: state.brokenSecretState ?? null,
    pendingLifeChargerPlayerId: state.pendingLifeChargerPlayerId ?? null,
  })
  const g = xmur3(payload)
  return g().toString()
}
